import { EmptySport, Loader } from 'components/scommessaFallback';
import { FilterTertiaryItemType, ItemType, ScommesseFilterTertiary } from 'components/scommesseFilterTertiary';
import { Icon, IconsEnum } from 'components/Icons';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { breakpoint, isHappybetSite, isSnaiSite } from 'utility/constant';
import { decodePathname, getScommesse, selectInfoTemplateManifestazione, selectIsFilterChanged } from 'features/sport';
import { isMatch, isNotEmpty, isTruthy } from 'utility/functions';
import { resetFilterAvvenimento, resetMenuManifestazioniFilters, toggleNavMobile } from 'features/sport/sportSlice';
import { selectIsVisualizzazioneEstesa } from 'features/settings/selectors';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';
import { useFilteredAvvenimentoCount, useInfoTemplateManifestazione, useInfoTipoList } from 'features/sport/hooks';
import { useGetLabelsByKeys, useTranslation } from 'hooks/useLingUI';

import { BannerHTML5, enBannerHTML5 } from 'components/bannerHTML5';
import { BannerHeroRenderer } from 'components/banner/bannerHero';
import { Button } from 'components/button';
import { Category } from 'types/categoryEnum';
import { DatoCmsSportManifestazionePage } from 'lib/datoCms/types';
import { DirettissimeCarousel } from 'components/direttissimeCarousel';
import { DynamicTeamsCarousel } from 'components/teamsCarousel';
import { HeaderTitleWithSearch } from 'components/headerTitleWithSearch';
import { Modal } from 'components/modals/components/modal';
import { ScommesseAccordionFilterPrimary } from 'components/scommesseAccordionFilterPrimary/ScommesseAccordionFilterPrimary';
import { ScommesseAccordionFilterSecondary } from 'components/scommesseAccordionFilterSecondary';
import { ScommesseFilterItem } from 'components/scommesseFiltersList';
import { ScommesseFiltersContainer } from './ScommesseFiltersContainer';
import { ScommesseFiltersList } from 'components/scommesseFiltersList';
import { ScommesseFiltersListScrollable } from 'components/scommesseFiltersListScrollable/ScommesseFiltersListScrollable';
import { ScommesseLayout } from 'features/scommesseLayout';
import { SportBoxWithSearch } from 'features/sport/components/sportBoxWithSearch';
import { SportComposer } from 'features/sport/components/sportComposer';
import { SportDisciplinaPageQuery } from 'lib/datoCms/queries/getSportDisciplinaPage';
import { SportInternalLayout } from 'features/sport/components/sportInternalLayout';
import { SportManifestazioneHeader } from 'features/sport/components/sportManifestazioneHeader';
import { SportManifestazioneHeaderSticky } from 'features/sport/components/sportManifestazioneHeaderSticky';
import { SportManifestazionePageQuery } from 'lib/datoCms/queries/getSportManifestazione';
import SportManifestazioneTable from './SportManifestazioneTable';
import { SportNews } from 'components/sportNews/SportNews';
import { SportTopNavigation } from 'features/sport/components/sportTopNavigation';
import { SportsCacheTemplateManifestazioneDto } from 'types/swagger';
import { TabsWithAccordion } from 'components/tabsWithAccordion';
import { TabsWithAccordionType } from 'lib/datoCms/queries/getSeoData';
import { Taxes } from 'features/sport/components/sportTicket/sportTicketSlice';
import { VisualizationEnum } from 'lib/api/sport/sportScommesseBySlugResponse';
import { errorLabelsUpdated } from 'features/carrello/carrelloSlice';
import { extractLabel } from 'features/sport/components/sportBoxWithSearch/utils/utils';
import { selectIsOpenCarrello } from 'features/carrello/carrelloSelectors';
import { selectIsSportSearchEnabled } from 'features/configuration/selectors';
import { setIsVisualizzazioneEstesa } from 'features/settings/settingsSlice';
import styles from './PageContent.module.scss';
import { useInternalSession } from 'hooks/useInternalSession';
import { useRouter } from 'next/router';
import useScommessaListScrollable from 'hooks/useScommessaListScrollable';
import useWindowSize, { enComparer } from 'hooks/useWindowSize';

interface RenderPageProps {
  sportManifestazionePage: DatoCmsSportManifestazionePage;
  // doCheck?: () => void;
}

const RenderPage = ({ sportManifestazionePage }: RenderPageProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { asPath } = useRouter();
  const [isWindowFiltersOpen, setIsWindowFiltersOpen] = useState(false);

  const [filterChecked, setFilterChecked] = useState<number>(0);
  const [selectedPrimaryFilter, setSelectedPrimaryFilter] = useState<number>(0);
  const [selectedSecondaryFilter, setSelectedSecondaryFilter] = useState<number>(0);
  const [checkedItems] = useState<undefined | { [name: string]: boolean }>({});

  const isOpen = useTypedSelector(selectIsOpenCarrello);
  const isVisualizzazioneEstesa = useTypedSelector(selectIsVisualizzazioneEstesa);

  const { templateManifestazione: template } = useTypedSelector(selectInfoTemplateManifestazione);

  const { doCheck } = useWindowSize();

  const filteredTipoScommessaList: Array<ScommesseFilterItem> = useMemo(
    () =>
      template?.gruppoList?.[selectedPrimaryFilter]?.tipoScommessaList?.map(
        ({ isNew, descrizione, descrizioneTrKey, toolTipInfoScommessaList }) => ({
          label: descrizione,
          labelTrKey: descrizioneTrKey,
          hasNew: isNew,
          scommesseDescription: toolTipInfoScommessaList,
        })
      ) ?? [],
    [selectedPrimaryFilter, template?.gruppoList]
  );

  const tipoScommessaListScrollable = useScommessaListScrollable(filteredTipoScommessaList);

  const infoTipoScommessaKeyList = useMemo(() => {
    const { tipoScommessaList, infoTipoScommessaKeyStringList } =
      template?.gruppoList?.[selectedPrimaryFilter]?.tipoScommessaList?.[selectedSecondaryFilter] ?? {};
    return tipoScommessaList?.[filterChecked]?.infoTipoScommessaKeyStringList ?? infoTipoScommessaKeyStringList ?? [];
  }, [template, selectedPrimaryFilter, selectedSecondaryFilter, filterChecked]);

  const { infoTipoScommessaList } = useInfoTipoList(infoTipoScommessaKeyList);

  const selectedGruppo = useMemo(
    () => template?.gruppoList?.[selectedPrimaryFilter],
    [selectedPrimaryFilter, template]
  );

  const selectedTipoScommessa = useMemo(
    () => template?.gruppoList?.[selectedPrimaryFilter]?.tipoScommessaList?.[selectedSecondaryFilter],
    [template, selectedPrimaryFilter, selectedSecondaryFilter]
  );

  const [isMobile, isDesktopXl] = doCheck([
    { comparer: enComparer.less, size: breakpoint.md },
    { comparer: enComparer.greater, size: breakpoint.xl },
  ]);

  const [slug, setSlug] = useState<string | undefined>(decodePathname(asPath));
  const [isMarcatore, setMarcatore] = useState(false);

  useEffect(() => {
    if (isNotEmpty(slug)) {
      const payload = {
        slug: slug!,
        isMarcatore: isTruthy(isMarcatore),
      };

      dispatch(getScommesse(payload));
    }
  }, [slug, isMarcatore, dispatch]);

  const isEstesaSwitchVisible = useMemo(() => {
    return (
      !isMarcatore &&
      infoTipoScommessaList.length === 1 &&
      infoTipoScommessaList[0].tipoVisualizzazione !== 1 &&
      infoTipoScommessaList[0].tipoVisualizzazione !== 3 &&
      infoTipoScommessaList[0].tipoVisualizzazione !== 6
    );
  }, [infoTipoScommessaList, isMarcatore]);

  const [chooseScommessaLabel, scommesseLabel, scommesseGroupLabel, estesaSwitchLabel] = useGetLabelsByKeys([
    'scegli-la-scommessa',
    'scommesse',
    'scommessa-group',
    'estesa-switch',
  ]);

  useEffect(() => {
    const nextSlug = selectedTipoScommessa?.tipoScommessaList?.[filterChecked]?.slug || selectedTipoScommessa?.slug;
    if (nextSlug !== slug) {
      setSlug(nextSlug);
    }
  }, [filterChecked, slug, selectedTipoScommessa?.slug, selectedTipoScommessa?.tipoScommessaList, setSlug]);

  useEffect(() => {
    const nextMarcatori = isTruthy(selectedGruppo?.isMarcatori);
    if (nextMarcatori !== isMarcatore) {
      setMarcatore(nextMarcatori);
    }
  }, [isMarcatore, selectedGruppo, setMarcatore]);

  const filteredGruppoList: Array<ScommesseFilterItem> = useMemo(
    () =>
      template?.gruppoList?.map(({ descrizione, descrizioneTrKey, isNew }) => ({
        label: descrizione,
        hasNew: isNew,
        labelTrKey: descrizioneTrKey,
      })) ?? [],
    [template?.gruppoList]
  );
  const gruppoScommessaListScrollable = useScommessaListScrollable(filteredGruppoList);

  const filters: Array<FilterTertiaryItemType> = useMemo(() => {
    return (
      template?.gruppoList?.[selectedPrimaryFilter]?.tipoScommessaList?.[selectedSecondaryFilter]?.tipoScommessaList ??
      []
    ).map(({ descrizione, descrizioneTrKey }) => {
      return {
        value: descrizione,
        label: descrizione,
        labelTrKey: descrizioneTrKey,
      };
    });
  }, [template?.gruppoList, selectedPrimaryFilter, selectedSecondaryFilter]);

  const headerVisualizationCondition = useMemo(() => {
    return (
      !isVisualizzazioneEstesa ||
      (isVisualizzazioneEstesa &&
        (infoTipoScommessaList?.length > 1 ||
          [
            VisualizationEnum.Antepost,
            VisualizationEnum.EsitiUnderThree,
            VisualizationEnum.MultipleInfoAggEsitiUnderThree,
          ].includes(infoTipoScommessaList[0]?.tipoVisualizzazione)))
    );
  }, [isVisualizzazioneEstesa, infoTipoScommessaList]);

  const boxSearchTipiScommessa = useTypedSelector(({ sport }) => sport.boxSearchTipiScommessa);

  useEffect(() => {
    if (boxSearchTipiScommessa.valueFilterSelected) {
      const indexPrimaryFilter = template?.gruppoList?.findIndex((gruppo) => {
        return gruppo.key === boxSearchTipiScommessa!.valueFilterSelected!.gruppoKey;
      });
      setSelectedPrimaryFilter(indexPrimaryFilter ? indexPrimaryFilter : 0);
      const indexSecondaryFilter = filteredTipoScommessaList?.findIndex((tipoScommessa) => {
        return tipoScommessa.label === boxSearchTipiScommessa.valueFilterSelected!.value;
      });
      setSelectedSecondaryFilter(indexSecondaryFilter ? indexSecondaryFilter : 0);
    }
  }, [
    boxSearchTipiScommessa,
    boxSearchTipiScommessa.valueFilterSelected,
    filteredTipoScommessaList,
    template?.gruppoList,
  ]);

  useEffect(() => {
    setSelectedPrimaryFilter(0);
    setSelectedSecondaryFilter(0);
  }, [asPath]);

  if (!template) return null;

  const itemsSelected = checkedItems
    ? checkedItems[
        `${filters.flatMap((filter: { label: string; value: string }) => filter.value)}` as keyof typeof checkedItems
      ]
    : false;

  return isMobile ? (
    <React.Fragment>
      <SportManifestazioneHeaderSticky
        gruppo={template.gruppo}
        isPreferito={false}
        gruppoTrKey={template.gruppoTrKey}
        providerID={template.externalProvider}
        manifestazione={template.manifestazione}
        manifestazioneTrKey={template.manifestazioneTrKey}
      />
      <ScommesseFiltersListScrollable
        selectedItem={selectedPrimaryFilter}
        color={'primary'}
        onSelectedElement={(index) => {
          setSelectedPrimaryFilter(index);
          setSelectedSecondaryFilter(0);
        }}
        className={styles.gruppoMobileContainer}
        list={gruppoScommessaListScrollable}
      />
      <ScommesseFiltersListScrollable
        limit={15}
        color={'white'}
        key={selectedPrimaryFilter}
        selectedItem={selectedSecondaryFilter}
        headerModalLabel={chooseScommessaLabel}
        className={styles.tipoScommessaMobileContainer}
        list={tipoScommessaListScrollable}
        onSelectedElement={(index) => setSelectedSecondaryFilter(index)}
        subHeaderModalLabel={t(selectedGruppo?.descrizioneTrKey, selectedGruppo?.descrizione)}
      />

      {/** potrebbe dinamico */}
      {isTruthy(filters.length) && isWindowFiltersOpen && (
        <Modal modalType="fullheight" modalCategory="mobile-filter-modal" onClose={() => setIsWindowFiltersOpen(false)}>
          <ScommesseFiltersContainer
            filterChecked={filterChecked}
            setFilterChecked={setFilterChecked}
            filters={filters}
            itemsSelected={itemsSelected}
          />
        </Modal>
      )}
      <div className={styles.wrapperFiltersEstesa}>
        {isTruthy(filters.length) && !isWindowFiltersOpen && (
          <div className={styles.filterButtonContainer}>
            <Button size="small" onClick={() => setIsWindowFiltersOpen(true)}>
              <Icon iconId={IconsEnum.FILTER} className={styles.filterButtonIcon} />
              Filtri
            </Button>
          </div>
        )}

        {/** potrebbe essere dinamica */}
        {isEstesaSwitchVisible && (
          <ScommesseFilterTertiary
            type={ItemType.toggle}
            items={[{ label: estesaSwitchLabel, value: 'Estesa', checked: isVisualizzazioneEstesa }]}
            className={styles.wrapper}
            classNameContainer={styles.estesaContainer}
            clickAction={() => {
              dispatch(setIsVisualizzazioneEstesa(!isVisualizzazioneEstesa));
            }}
          />
        )}
      </div>

      <SportManifestazioneTable isMarcatore={isMarcatore} infoTipoScommessaKeyList={infoTipoScommessaKeyList} />

      {sportManifestazionePage?.sportNewsBlock && (
        <SportNews
          url={sportManifestazionePage?.sportNewsBlock.url}
          title={sportManifestazionePage.sportNewsBlock.title}
        />
      )}
    </React.Fragment>
  ) : (
    <React.Fragment>
      <SportManifestazioneHeader
        idGruppo={template.idGruppo}
        gruppo={template?.gruppo}
        gruppoTrKey={template?.gruppoTrKey}
        manifestazione={template?.manifestazione}
        manifestazioneTrKey={template?.manifestazioneTrKey}
        providerId={template?.externalProvider}
        iconSrc={template?.iconSrc}
        noPaddingRight={isOpen}
        idManifestazione={template?.idManifestazione}
        slugManifestazione={template.slug}
      />
      <SportBoxWithSearch
        isActive={isSnaiSite}
        slug={asPath}
        labelsBoxWithSearch={extractLabel(sportManifestazionePage)}
        data-parent
        noPaddingRight={isOpen || isDesktopXl}
      >
        <React.Fragment>
          <div className={styles.filtersContainer}>
            <ScommesseAccordionFilterPrimary
              label={scommesseGroupLabel}
              hasShowMore={filteredGruppoList!.length > 8}
              selectedFilter={selectedGruppo?.descrizione}
              selectedFilterTrKey={selectedGruppo?.descrizioneTrKey}
              hasMarginBotton
              isAccordionOpened={boxSearchTipiScommessa.isAccordionFilterClosed}
            >
              <ScommesseFiltersList
                selectedIndex={selectedPrimaryFilter}
                onSelectedFilter={async (selectedFilter: number) => {
                  setSelectedPrimaryFilter(selectedFilter);
                  setFilterChecked(0);
                  setSelectedSecondaryFilter(0);
                }}
                items={filteredGruppoList!}
              />
            </ScommesseAccordionFilterPrimary>
            <ScommesseAccordionFilterSecondary
              label={scommesseLabel}
              selectedFilter={selectedTipoScommessa?.descrizione!}
              selectedFilterTrKey={selectedTipoScommessa?.descrizioneTrKey}
              hasMarginBottom
              isAccordionClosed={boxSearchTipiScommessa.isAccordionFilterClosed}
            >
              <ScommesseFiltersList
                onSelectedFilter={(selectedSecondaryFilterIndex: number) => {
                  setFilterChecked(0);
                  setSelectedSecondaryFilter(selectedSecondaryFilterIndex);
                }}
                selectedIndex={selectedSecondaryFilter}
                items={filteredTipoScommessaList!}
              />
            </ScommesseAccordionFilterSecondary>
          </div>
          {isTruthy(filters?.length) && (
            <ScommesseFiltersContainer
              filters={filters}
              filterChecked={filterChecked}
              itemsSelected={itemsSelected}
              setFilterChecked={setFilterChecked}
            />
          )}
          {isEstesaSwitchVisible && (
            <ScommesseFilterTertiary
              items={[{ label: estesaSwitchLabel, value: 'Estesa', checked: isVisualizzazioneEstesa }]}
              type={ItemType.toggle}
              clickAction={() => {
                dispatch(setIsVisualizzazioneEstesa(!isVisualizzazioneEstesa));
              }}
              classNameContainer={styles.estesaContainer}
            />
          )}
          <SportManifestazioneTable
            isMarcatore={isMarcatore}
            suppressLoader
            isHeaderVisible={headerVisualizationCondition}
            infoTipoScommessaKeyList={infoTipoScommessaKeyList}
          />
        </React.Fragment>
      </SportBoxWithSearch>
      {sportManifestazionePage?.sportNewsBlock && (
        <SportNews
          title={sportManifestazionePage.sportNewsBlock.title}
          url={sportManifestazionePage?.sportNewsBlock.url}
        />
      )}
    </React.Fragment>
  );
};

type Props = SportManifestazionePageQuery &
  SportDisciplinaPageQuery & {
    taxes?: Taxes;
    seoData?: TabsWithAccordionType;
  };

const PageContent = ({ taxes, seoData, sportNav, allCarrelloErrors, sportManifestazionePage }: Props) => {
  const dispatch = useAppDispatch();
  const { id, heroBanner, visualComposer } = sportManifestazionePage ?? {};
  const { asPath } = useRouter();
  const { isAuthenticated } = useInternalSession();
  const { isMobile } = useWindowSize();

  // template manifestazione
  const { slug: pageSlug, disciplina } = useMemo(() => {
    const slug = decodePathname(asPath);
    const [_, disciplina] = slug.split('/');
    return { slug, disciplina };
  }, [asPath]);

  const {
    isTemplateLoading,
    isScommessaLoading,
    templateManifestazione: template,
  } = useInfoTemplateManifestazione<SportsCacheTemplateManifestazioneDto>();

  useEffect(() => {
    dispatch(errorLabelsUpdated(allCarrelloErrors));
  }, [dispatch, allCarrelloErrors]);

  useEffect(() => {
    if (isMobile) dispatch(toggleNavMobile(false));
  }, [dispatch, isMobile]);

  const refContainer = useRef<HTMLDivElement>(null);

  const isSportSearchEnabled = useTypedSelector(selectIsSportSearchEnabled);

  const isFilterChanged = useTypedSelector(selectIsFilterChanged);
  const resetFilters = () => {
    dispatch(resetFilterAvvenimento());
    dispatch(resetMenuManifestazioniFilters());
  };

  const { counter } = useFilteredAvvenimentoCount();

  return (
    <ScommesseLayout taxes={taxes} disciplina={template?.disciplina} hasNoPaddingOnMain>
      <HeaderTitleWithSearch
        title={'Sport'} // TODO Cambiare
        category={Category.Sport}
        labelSearch={isSportSearchEnabled ? 'Cerca' : null}
        hasPaddingLeft={isMobile}
        titleHighlighted={template?.disciplina ?? disciplina}
        titleHighlightedTrKey={template?.disciplinaTrKey!}
      />
      {isTruthy(heroBanner?.[0]?.banners?.length) && <BannerHeroRenderer pageId={id} heroBanner={heroBanner[0]!} />}
      <SportTopNavigation hasFirstElementFixed={!isHappybetSite} />
      {isMobile && <BannerHTML5 url={enBannerHTML5.Sport} />}
      <SportInternalLayout
        sportNav={sportNav}
        disciplina={template?.disciplina ?? disciplina}
        disciplinaTrKey={template?.disciplinaTrKey}
      >
        <div ref={refContainer}>
          <RenderPage sportManifestazionePage={sportManifestazionePage} />
          {isTruthy(visualComposer?.length) && (
            <SportComposer
              key={`Composer-${asPath}`}
              pageId={id}
              cmsData={visualComposer ?? []}
              templateSlug="templateManifestazione"
            />
          )}
          <Loader
            key={`loader-${id}-${isTemplateLoading}-${isScommessaLoading}-${asPath}`}
            showOnScommessaLoading={!isTruthy(counter)}
          />
        </div>
        <EmptySport
          key={`empty-${id}-${isTemplateLoading}-${isScommessaLoading}-${asPath}`}
          container={refContainer.current}
          hasFilter={isFilterChanged}
          hasRedirect={!isTruthy(template?.gruppoList?.length)}
          resetFilter={resetFilters}
        />
        {sportManifestazionePage?.direttissimeBlock && (
          <DirettissimeCarousel
            title={sportManifestazionePage.direttissimeBlock.title}
            className="direttissimeContainer"
            slug={pageSlug}
            isEnabledPromo={sportManifestazionePage.direttissimeBlock.promoEnabled}
          />
        )}
        {isMatch(disciplina, '^CALCIO$') && template && isAuthenticated && sportManifestazionePage?.squadreBlock && (
          <DynamicTeamsCarousel
            slug={pageSlug}
            title={`${template.manifestazione}: ${sportManifestazionePage?.squadreBlock?.title}`}
            className="topManifestazioneContainer"
            idDisciplina={template.idDisciplina}
            idManifestazione={template.idManifestazione}
          />
        )}
        <TabsWithAccordion tabs={seoData?.tabs ?? []} />
      </SportInternalLayout>
    </ScommesseLayout>
  );
};

export default PageContent;
